import { useMemo, useRef } from 'react';

import { FieldErrors, FieldValues } from 'react-hook-form';

import { BaseOnboardingEntity } from '@/modules/onboarding/common/types/base-onboarding-entity';
import { EntityValidationReportResponse } from '@/modules/onboarding/common/types/entity-validation-report.response';
import { findDuplicateIndices } from '@/modules/onboarding/common/utils/find-duplicate-indices';

import { UiEntitiesFilter } from './use-entities-query-filters';

type DuplicateFieldExtractor = (item: any) => string;

/**
 * Result of client-side validation analysis.
 * Contains both the merged report (for filter tab counts) and the sets of
 * entity IDs that match each filter (for client-side filtering).
 */
export interface ClientSideValidationResult {
  /** Merged validation report for filter tab badge counts */
  report: EntityValidationReportResponse;
  /** IDs of entities with field-level validation errors (excluding duplicate-only errors) */
  errorEntityIds: Set<number>;
  /** IDs of entities that are duplicates */
  duplicateEntityIds: Set<number>;
  /** IDs of entities missing connected integrations */
  warningEntityIds: Set<number>;
}

/**
 * Finds entity IDs that have field-level form errors, excluding entities
 * whose only errors are duplicate refinement errors.
 */
function findEntitiesWithFieldErrors<
  TEntity extends BaseOnboardingEntity,
  TFormValues extends FieldValues,
>(
  formErrors: FieldErrors<TFormValues>,
  formFieldPrefix: string,
  entities: TEntity[],
  duplicateEntityIds: Set<number>
): Set<number> {
  const errorEntityIds = new Set<number>();
  const fieldArrayErrors = (formErrors as any)?.[formFieldPrefix];
  if (!Array.isArray(fieldArrayErrors)) return errorEntityIds;

  fieldArrayErrors.forEach((entityErrors: Record<string, any> | undefined, index: number) => {
    if (!entityErrors || Object.keys(entityErrors).length === 0) return;
    if (index >= entities.length) return;

    const entityId = entities[index].id;

    if (!duplicateEntityIds.has(entityId)) {
      errorEntityIds.add(entityId);
      return;
    }

    // Entity is also a duplicate. Count as error only if it has a non-duplicate error.
    // Duplicate superRefine produces errors with type 'custom'. Field validators
    // produce 'too_small', 'too_big', 'invalid_string', etc.
    for (const fieldError of Object.values(entityErrors)) {
      if (!fieldError || typeof fieldError !== 'object') continue;
      const err = fieldError as Record<string, any>;
      if (err.type && err.type !== 'custom') {
        errorEntityIds.add(entityId);
        return;
      }
    }
  });

  return errorEntityIds;
}

/**
 * Finds entity IDs of duplicate entities by checking form values for case-insensitive matches.
 */
function findDuplicateEntityIds<TEntity extends BaseOnboardingEntity>(
  formValues: any[],
  entities: TEntity[],
  fieldExtractors: DuplicateFieldExtractor[]
): Set<number> {
  const duplicateEntityIds = new Set<number>();

  if (!formValues || formValues.length === 0) return duplicateEntityIds;

  // Trim form values to the number of known entities so stale entries from
  // recently-deleted rows (form reset hasn't propagated yet) don't produce
  // false duplicate matches against surviving entities.
  const alignedValues = formValues.slice(0, entities.length);

  for (const extractor of fieldExtractors) {
    const values = alignedValues.map(item => {
      const val = extractor(item);
      return val ? val.trim().toLowerCase() : '';
    });

    for (const index of findDuplicateIndices(values)) {
      if (index < entities.length) {
        duplicateEntityIds.add(entities[index].id);
      }
    }
  }

  return duplicateEntityIds;
}

interface UseClientSideValidationReportParams<
  TEntity extends BaseOnboardingEntity,
  TFormValues extends FieldValues,
> {
  /** Backend validation report (server-side counts) */
  backendReport: EntityValidationReportResponse;
  /** Current form errors from react-hook-form */
  formErrors: FieldErrors<TFormValues>;
  /** The form field array prefix (e.g. 'employees', 'clients', 'projects') */
  formFieldPrefix: string;
  /** Current form values for the field array */
  formFieldValues: any[];
  /** Functions that extract fields to check for duplicates (e.g. name, email) */
  duplicateFieldExtractors: DuplicateFieldExtractor[];
  /** Currently loaded entities (to check integration warnings and map indices to IDs) */
  entities: TEntity[];
  /** Known issue IDs observed in the loaded form, grouped by category */
  knownIssueEntityIds?: Partial<Record<UiEntitiesFilter, Set<number>>>;
  /** Number of connected integrations for this entity type */
  connectedIntegrationsCount: number;
}

/**
 * Computes a merged validation report that combines backend counts with
 * real-time client-side form state. Also returns entity ID sets for each filter
 * category so the step component can filter the entity list client-side.
 */
/**
 * Compares two Sets by value — returns true if they contain the same elements.
 */
function areSetsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function countResolvedBackendEntities(
  knownIssueEntityIds: Set<number> | undefined,
  currentIssueEntityIds: Set<number>
): number {
  if (!knownIssueEntityIds || knownIssueEntityIds.size === 0) {
    return 0;
  }

  let resolvedCount = 0;
  knownIssueEntityIds.forEach(entityId => {
    if (!currentIssueEntityIds.has(entityId)) {
      resolvedCount += 1;
    }
  });

  return resolvedCount;
}

function getMergedCategoryCount({
  backendCount,
  currentIssueEntityIds,
  knownIssueEntityIds,
}: {
  backendCount: number;
  currentIssueEntityIds: Set<number>;
  knownIssueEntityIds: Set<number> | undefined;
}): number {
  if (!knownIssueEntityIds || knownIssueEntityIds.size === 0) {
    return Math.max(backendCount, currentIssueEntityIds.size);
  }

  const resolvedBackendCount = countResolvedBackendEntities(
    knownIssueEntityIds,
    currentIssueEntityIds
  );

  return Math.max(Math.max(0, backendCount - resolvedBackendCount), currentIssueEntityIds.size);
}

export function useClientSideValidationReport<
  TEntity extends BaseOnboardingEntity,
  TFormValues extends FieldValues,
>({
  backendReport,
  formErrors,
  formFieldPrefix,
  formFieldValues,
  duplicateFieldExtractors,
  entities,
  knownIssueEntityIds,
  connectedIntegrationsCount,
}: UseClientSideValidationReportParams<TEntity, TFormValues>): ClientSideValidationResult {
  // Compute on every render (formErrors/formFieldValues are unstable references from RHF).
  const duplicateEntityIds = findDuplicateEntityIds(
    formFieldValues,
    entities,
    duplicateFieldExtractors
  );

  const errorEntityIds = findEntitiesWithFieldErrors(
    formErrors,
    formFieldPrefix,
    entities,
    duplicateEntityIds
  );

  const warningEntityIds = useMemo(() => {
    const ids = new Set<number>();
    if (connectedIntegrationsCount > 0) {
      entities.forEach(entity => {
        if (entity.connectedIntegrations.length < connectedIntegrationsCount) {
          ids.add(entity.id);
        }
      });
    }
    return ids;
  }, [entities, connectedIntegrationsCount]);

  // Stabilise the result: only return a new object when the entity ID sets
  // or report numbers actually changed. This prevents downstream useMemos
  // (e.g. `entities` for the table) from recomputing on every render, which
  // would cause TanStack Table to remount rows and lose input focus.
  const prevRef = useRef<ClientSideValidationResult | null>(null);

  const report = {
    total: backendReport.total,
    errorsCount: getMergedCategoryCount({
      backendCount: backendReport.errorsCount,
      currentIssueEntityIds: errorEntityIds,
      knownIssueEntityIds: knownIssueEntityIds?.[UiEntitiesFilter.ERRORS],
    }),
    duplicatesCount: getMergedCategoryCount({
      backendCount: backendReport.duplicatesCount,
      currentIssueEntityIds: duplicateEntityIds,
      knownIssueEntityIds: knownIssueEntityIds?.[UiEntitiesFilter.DUPLICATES],
    }),
    warningsCount: getMergedCategoryCount({
      backendCount: backendReport.warningsCount,
      currentIssueEntityIds: warningEntityIds,
      knownIssueEntityIds: knownIssueEntityIds?.[UiEntitiesFilter.WARNINGS],
    }),
  };

  const prev = prevRef.current;
  if (
    prev &&
    prev.report.total === report.total &&
    prev.report.errorsCount === report.errorsCount &&
    prev.report.duplicatesCount === report.duplicatesCount &&
    prev.report.warningsCount === report.warningsCount &&
    areSetsEqual(prev.errorEntityIds, errorEntityIds) &&
    areSetsEqual(prev.duplicateEntityIds, duplicateEntityIds) &&
    areSetsEqual(prev.warningEntityIds, warningEntityIds)
  ) {
    return prev;
  }

  const next: ClientSideValidationResult = {
    report,
    errorEntityIds,
    duplicateEntityIds,
    warningEntityIds,
  };
  prevRef.current = next;
  return next;
}

/**
 * Filters entities based on the active UI filter, merging backend results
 * with client-side detected entities. Deduplicates by entity ID.
 *
 * @param pinnedEntityIds - Entity IDs that must remain in the list regardless of their
 * current validation state (e.g. entities being actively edited). Prevents rows from
 * disappearing mid-edit when the user clears a field that was previously an error.
 */
export function mergeFilteredEntities<TEntity extends BaseOnboardingEntity>(
  backendEntities: TEntity[],
  allEntities: TEntity[],
  uiFilter: UiEntitiesFilter | null,
  validation: ClientSideValidationResult,
  pinnedEntityIds?: Set<number>
): TEntity[] {
  if (!uiFilter) return backendEntities;

  let matchingIds: Set<number>;
  switch (uiFilter) {
    case UiEntitiesFilter.DUPLICATES:
      matchingIds = validation.duplicateEntityIds;
      break;
    case UiEntitiesFilter.ERRORS:
      matchingIds = validation.errorEntityIds;
      break;
    case UiEntitiesFilter.WARNINGS:
      matchingIds = validation.warningEntityIds;
      break;
    default:
      return backendEntities;
  }

  // Combine current matches with pinned IDs so rows being edited stay visible
  const effectiveIds =
    pinnedEntityIds && pinnedEntityIds.size > 0
      ? new Set([...matchingIds, ...pinnedEntityIds])
      : matchingIds;
  const loadedEntityIds = new Set(allEntities.map(entity => entity.id));

  // Client-side filtered entities from the "all" list
  const clientFiltered = allEntities.filter(entity => effectiveIds.has(entity.id));

  // Merge with backend results, dedup by ID
  const seenIds = new Set<number>();
  const merged: TEntity[] = [];

  for (const entity of backendEntities) {
    const shouldKeepBackendEntity = effectiveIds.has(entity.id) || !loadedEntityIds.has(entity.id);

    if (shouldKeepBackendEntity && !seenIds.has(entity.id)) {
      seenIds.add(entity.id);
      merged.push(entity);
    }
  }

  for (const entity of clientFiltered) {
    if (!seenIds.has(entity.id)) {
      seenIds.add(entity.id);
      merged.push(entity);
    }
  }

  return merged;
}
